/* eslint-disable camelcase */
/* eslint-disable fp/no-mutation */
import {
Alert, Button, Icon, Modal, Spinner
} from '@nike/eds';
import PropTypes from 'prop-types';
import React, {
useEffect, useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNposValue, setPrintJobInfo } from '../../../redux/actions';
import { getBodyTranslations, getSessionContext } from '../../../redux/selectors';
import { buildReprintEventPayload } from '../../../utils/buildReprintPayload';
import { NPOS, WORKSTATION_TYPE_CODES } from '../../../utils/constants';
import { fetchReceiptForTransaction, reportReprintEvent } from '../../../utils/service-calls';
import { EJ_NPOS_REQUEST, postWkMessage } from '../../../utils/webkitMessaging';
import './Print-receipt-modal.css';

const SHOULD_REPORT_REPRINT_EVENT = false;

const dynamicFetchReceipt = async (storeCountry, storeId, transactionID, authToken, workStationType) => {
  // Check if the workstation type is NikePOSRegister call with regular transactionID
  if (WORKSTATION_TYPE_CODES.NikePOSRegister === workStationType) {
    const responseNPOS = await fetchReceiptForTransaction(storeCountry, storeId, `${transactionID}`, authToken);
    return responseNPOS;
  }
  // First try with zplImg| prefix
  const rawTran = `zplImg|${transactionID}`;
  const encodedValue = encodeURIComponent(rawTran);

  const responseZplImg = await fetchReceiptForTransaction(storeCountry, storeId, encodedValue, authToken);
  if (responseZplImg?.body[0]?.printedReceipt) {
    return responseZplImg;
  }
  // If no printedReceipt, try without prefix
  const response = await fetchReceiptForTransaction(storeCountry, storeId, `${transactionID}`, authToken);
  return response;
};

const PrintReceiptModal = ({
  isPrinterDialogOpen, storeInfo, transactionBlob, authToken
}) => {
    const printJobInfo = useSelector((state) => state.nposValues.printJobInfo);

    const {
     RL_PrintFailure, RL_PrintSuccess, RL_PrintReceiptInProgress, RL_PrintReceipt, RL_ReceiptUnavailable, RL_Ok
    } = useSelector(getBodyTranslations);

    const sessionContext = useSelector(getSessionContext);
    const printStatus = printJobInfo?.status;

    const isPrintingInProgress = printStatus === NPOS.PRINT_STATUS.INITIATED;

    const dispatch = useDispatch();
    const reprintEventSent = useRef(false);

    const setIsPrinterDialogOpen = (value) => dispatch(setNposValue({ isPrinterDialogOpen: value }));

     useEffect(() => {
      const updatePrintStatus = (printJobStatusObj) => dispatch(setPrintJobInfo(printJobStatusObj));
      const printImage = (command) => {
        const basicPayloadV1 = { transactionId: transactionBlob?.Transaction?.TransactionID };
        const basicPayloadV2 = { ...basicPayloadV1, encoding: 'BASE_64', printerLanguage: 'IMAGE' };
        // In future we will be removing support for "commandForSingleReceipt"
        const commandForSingleReceipt = command ? { receiptImgBuffer: JSON.parse(command)?.regular[0], ...basicPayloadV1 } : null;
        const commandForMultipleReceipts = command ? { receiptsPayload: JSON.parse(command), ...basicPayloadV2 } : null;

        updatePrintStatus({ status: NPOS.PRINT_STATUS.INITIATED });

        postWkMessage(EJ_NPOS_REQUEST.REPRINT_RECEIPT, commandForSingleReceipt);
        postWkMessage(EJ_NPOS_REQUEST.REPRINT_RECEIPT_V2, commandForMultipleReceipts);
      };
      const onPrintImageClick = async () => {
        updatePrintStatus({ status: NPOS.PRINT_STATUS.INITIATED });
        try {
          const transactionID = transactionBlob?.Transaction?.TransactionID;
          const receipt = await dynamicFetchReceipt(storeInfo.selectedCountry.value, storeInfo.selectedStore.value, transactionID, authToken, transactionBlob?.Transaction?.Workstation?.TypeCode);
          if (receipt?.body[0]?.format === 'IMAGE') {
            printImage(receipt?.body[0]?.printedReceipt);
          } else {
            updatePrintStatus({ error: RL_ReceiptUnavailable, status: NPOS.PRINT_STATUS.ERROR });
          }
        } catch (err) {
          updatePrintStatus({ error: 'An error occurred while printing the receipt.', status: NPOS.PRINT_STATUS.ERROR });
        }
      };

      onPrintImageClick();
    }, [transactionBlob, authToken, dispatch, storeInfo, RL_ReceiptUnavailable]);

    useEffect(() => {
      if (printStatus !== NPOS.PRINT_STATUS.SUCCESS) {
        reprintEventSent.current = false;
        return;
      }
      if (!SHOULD_REPORT_REPRINT_EVENT) return;
      if (reprintEventSent.current) return;
      if (!sessionContext) return;

      const transaction = transactionBlob?.Transaction;
      if (!transaction) return;

      reprintEventSent.current = true;
      const payload = buildReprintEventPayload(transaction, sessionContext);
      reportReprintEvent(storeInfo.selectedCountry?.value, payload, authToken)
        .then((res) => {
          // eslint-disable-next-line no-console
          console.log('data/injest successful', res);
          return res;
        })
        // eslint-disable-next-line no-console
        .catch((err) => console.error('Failed to report reprint event:', err));
    }, [printStatus, sessionContext, transactionBlob, storeInfo, authToken]);

    const onDismiss = () => {
      if (isPrintingInProgress) return;
      setIsPrinterDialogOpen(false);
      dispatch(setNposValue({ printJobInfo: { ...printJobInfo, error: null, status: null } }));
    };

    return (
      <div className="printer-modal" id="printer-modal">
        <Modal
          hideFade
          footerSlot={!isPrintingInProgress ? (
            <Button
              size="medium"
              variant="secondary"
              onClick={onDismiss}
            >
              {RL_Ok}
            </Button>
          ) : (<></>)}
          headerSlot={(
            <div className="d-sm-flx flx-ai-sm-c">
              <Icon name="Print" size="l" />
              <p className="pl2-sm">{RL_PrintReceipt}</p>
            </div>
          )}
          isOpen={isPrinterDialogOpen}
          onDismiss={onDismiss}
        >
          {isPrintingInProgress
          && (
            <div className="d-sm-flx flx-dir-sm-c flx-ai-sm-c" style={{ gap: '6px' }}>
              <Spinner size="large" />
              <p className="eds-type--body-1">{RL_PrintReceiptInProgress }</p>
            </div>
          )}

          {!isPrintingInProgress && printStatus === NPOS.PRINT_STATUS.SUCCESS
          && (
          <div className="d-sm-flx flx-dir-sm-c flx-ai-sm-c" style={{ gap: '6px' }}>
            <Icon color="#007D48" name="CheckCircleFilled" size="l" />
            <p className="eds-type--body-1">{RL_PrintSuccess}</p>
          </div>
          )}

          {!isPrintingInProgress && printJobInfo?.error && (
            <div className="d-sm-flx flx-dir-sm-c flx-ai-sm-c" style={{ gap: '6px' }}>
              <Icon name="AlertCircle" size="l" />
              <p className="eds-type--body-1">{RL_PrintFailure}</p>
              <div className="d-sm-flx flx-jc-sm-c">
                <Alert quiet status="danger">
                  {printJobInfo?.error}
                </Alert>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  };

  PrintReceiptModal.propTypes = {
    authToken: PropTypes.string.isRequired,
    isPrinterDialogOpen: PropTypes.bool.isRequired,
    storeInfo: PropTypes.shape().isRequired,
    transactionBlob: PropTypes.shape().isRequired
  };

export default PrintReceiptModal;
